import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Search, Loader2, Calendar, MapPin, Crown, Ban, CreditCard, CheckCircle2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import apiClient, { SERVICES_META, CONTACT } from "@/lib/api";
import { toast } from "sonner";

export default function FindBooking() {
  const [bookingId, setBookingId] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState("");
  const [payingBalance, setPayingBalance] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBooking(null);
    setLoading(true);
    try {
      const { data } = await apiClient.post("/bookings/lookup", {
        booking_id: bookingId.trim(),
        email: email.trim(),
      });
      setBooking(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "No matching booking found");
    } finally {
      setLoading(false);
    }
  };

  const cancel = async () => {
    if (!booking) return;
    const ok = window.confirm(
      "Are you sure you want to cancel this booking? Deposits are non-refundable outside the 5-day grace period."
    );
    if (!ok) return;
    try {
      const { data } = await apiClient.post(`/bookings/${booking.id}/cancel`, { email });
      toast.success(data.message);
      const refreshed = await apiClient.post("/bookings/lookup", { booking_id: booking.id, email });
      setBooking(refreshed.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not cancel");
    }
  };

  const payBalance = async () => {
    if (!booking) return;
    setPayingBalance(true);
    try {
      const { data } = await apiClient.post("/payments/balance-checkout", {
        booking_id: booking.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not start balance payment");
      setPayingBalance(false);
    }
  };

  const cancellableUntil = booking ? new Date(booking.cancellable_until) : null;
  const withinWindow = cancellableUntil ? new Date() <= cancellableUntil : false;

  return (
    <div className="bg-[#050505] min-h-screen text-white" data-testid="find-booking-page">
      <Navbar />
      <section className="pt-32 pb-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-3xl mx-auto">
          <span className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Manage Your Booking</span>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light mt-3 mb-3">
            Find your <span className="italic gold-gradient-text">reservation.</span>
          </h1>
          <p className="text-zinc-400 mb-12 leading-relaxed">
            Enter the booking ID from your confirmation along with the email used to book.
          </p>

          <form onSubmit={submit} className="lk-card p-8 grid md:grid-cols-2 gap-x-8 gap-y-6 mb-10">
            <div>
              <label className="text-xs uppercase tracking-widest text-[#D4AF37] mb-2 block">Booking ID</label>
              <input
                type="text"
                required
                value={bookingId}
                onChange={(e) => setBookingId(e.target.value)}
                className="lk-input"
                data-testid="lookup-booking-id"
                placeholder="e.g. 8b2f-..."
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#D4AF37] mb-2 block">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="lk-input"
                data-testid="lookup-email"
              />
            </div>
            <div className="md:col-span-2 flex items-center justify-between mt-2">
              {error && <span className="text-xs text-red-400" data-testid="lookup-error">{error}</span>}
              <button
                type="submit"
                disabled={loading}
                className="lk-btn-primary text-xs inline-flex items-center gap-2 ml-auto disabled:opacity-50"
                data-testid="lookup-submit"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                Look Up
              </button>
            </div>
          </form>

          {booking && (
            <div className="lk-card p-8 space-y-6" data-testid="lookup-result">
              <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                  <span className="text-xs uppercase tracking-widest text-[#D4AF37]">
                    {SERVICES_META[booking.service]?.title || booking.service}
                  </span>
                  <h2 className="font-serif text-3xl mt-1">{booking.full_name}</h2>
                </div>
                <div className="text-right">
                  <span className={`text-xs uppercase tracking-widest ${booking.status === "cancelled" ? "text-zinc-500" : booking.payment_status === "paid" ? "text-green-400" : "text-yellow-400"}`}>
                    {booking.status} ·{" "}
                    {booking.balance_paid
                      ? "fully paid"
                      : booking.payment_status === "paid"
                      ? "deposit paid"
                      : booking.payment_status}
                  </span>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <Detail icon={Calendar} label="Date" value={`${booking.event_date} at ${booking.start_time}`} />
                <Detail icon={Crown} label="Duration" value={`${booking.hours} hours`} />
                <Detail icon={MapPin} label="Venue" value={booking.venue} />
                <Detail label="Prints" value={booking.prints} />
                <Detail label="Themed Props" value={booking.themed_props ? booking.theme || "Yes" : "No"} />
              </div>

              <div className="grid sm:grid-cols-3 gap-4 border-t border-[#D4AF37]/15 pt-6">
                <Money label="Event Total" value={booking.total_amount} />
                <Money label="Deposit Paid" value={booking.payment_status === "paid" ? booking.deposit_amount : 0} highlight />
                <Money
                  label="Balance Due"
                  value={booking.balance_paid ? 0 : booking.payment_status === "paid" ? booking.balance_amount : booking.total_amount}
                />
              </div>
              {booking.discount_amount > 0 && (
                <div className="text-xs text-[#D4AF37] tracking-widest uppercase" data-testid="lookup-discount">
                  Promo {booking.promo_code} applied — saved ${Number(booking.discount_amount).toFixed(2)}
                </div>
              )}

              {booking.status !== "cancelled" && booking.payment_status === "paid" && !booking.balance_paid && (
                <div
                  className="border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-6 flex items-center justify-between flex-wrap gap-4"
                  data-testid="balance-due-panel"
                >
                  <div className="text-sm text-zinc-300 max-w-md leading-relaxed">
                    <strong className="text-[#D4AF37]">Balance due on event day.</strong>{" "}
                    Settle your remaining ${Number(booking.balance_amount).toFixed(2)} securely with Stripe.
                  </div>
                  <button
                    onClick={payBalance}
                    disabled={payingBalance}
                    className="lk-btn-primary text-xs inline-flex items-center gap-2 disabled:opacity-50"
                    data-testid="pay-balance-btn"
                  >
                    {payingBalance ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                    Pay Balance
                  </button>
                </div>
              )}

              {booking.balance_paid && (
                <div
                  className="border border-green-500/30 bg-green-500/5 p-4 flex items-center gap-3 text-xs uppercase tracking-widest text-green-400"
                  data-testid="fully-paid-badge"
                >
                  <CheckCircle2 className="w-4 h-4" /> Fully paid — see you on event day
                </div>
              )}

              {booking.status !== "cancelled" && (
                <div className="border-t border-[#D4AF37]/15 pt-6 flex items-center justify-between flex-wrap gap-4">
                  <div className="text-xs text-zinc-400 max-w-md leading-relaxed">
                    <strong className="text-zinc-200">Cancellation:</strong>{" "}
                    {withinWindow
                      ? `Full deposit refund available until ${cancellableUntil.toLocaleDateString()}.`
                      : "Outside the 5-day grace period — deposit non-refundable."}
                  </div>
                  <button
                    onClick={cancel}
                    className="lk-btn-secondary text-xs inline-flex items-center gap-2"
                    data-testid="lookup-cancel"
                  >
                    <Ban className="w-3.5 h-3.5" /> Cancel Booking
                  </button>
                </div>
              )}
            </div>
          )}

          <p className="mt-10 text-xs text-zinc-500 text-center">
            Lost your booking ID?{" "}
            <a href={CONTACT.emailHref} className="text-[#D4AF37] hover:underline">
              Email us
            </a>{" "}
            or call <a href={CONTACT.phoneHref} className="text-[#D4AF37] hover:underline">{CONTACT.phone}</a>.
          </p>

          <div className="text-center mt-12">
            <Link to="/" className="text-xs uppercase tracking-widest text-zinc-500 hover:text-[#D4AF37]">
              ← Back to home
            </Link>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}

function Detail({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      {Icon && <Icon className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" />}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
        <div className="text-zinc-200">{value}</div>
      </div>
    </div>
  );
}

function Money({ label, value, highlight }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-serif text-2xl mt-1 ${highlight ? "gold-gradient-text" : "text-white"}`}>
        ${Number(value || 0).toFixed(2)}
      </div>
    </div>
  );
}
